import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AffiliateShell from "@/components/feature/AffiliateShell";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { fetchApprovedMessages, type ApprovedMessage } from "@/services/admin";
import {
  fetchPersonalityProfiles,
  fetchReferredProspects,
  suggestedMessage,
  type InterestLevel,
  type PersonalityInfo,
  type ProspectState,
  type ReferredProspect,
} from "@/services/affiliate";

const PURPLE = "#7C3AED";
const WEEK = 7 * 24 * 60 * 60 * 1000;

type TabKey = "todos" | "nuevo" | "seguimiento" | "caliente" | "reuniones" | "inactivo";

const STATE_UI: Record<ProspectState, { label: string; icon: string; cls: string }> = {
  caliente: { label: "Lead caliente", icon: "ri-fire-fill", cls: "bg-emerald-50 text-emerald-700" },
  seguimiento: { label: "En seguimiento", icon: "ri-time-line", cls: "bg-amber-50 text-amber-600" },
  nuevo: { label: "Nuevo", icon: "ri-user-add-line", cls: "bg-sky-50 text-sky-600" },
  inactivo: { label: "No activo", icon: "ri-user-unfollow-line", cls: "bg-slate-100 text-slate-500" },
};

const INTEREST_UI: Record<InterestLevel, { label: string; icon: string; color: string }> = {
  muy_alto: { label: "Muy alto", icon: "ri-fire-fill", color: "text-rose-500" },
  alto: { label: "Alto", icon: "ri-fire-fill", color: "text-orange-500" },
  medio: { label: "Medio", icon: "ri-trophy-fill", color: "text-amber-500" },
  bajo: { label: "Bajo", icon: "ri-fire-fill", color: "text-sky-500" },
};

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function activityText(p: ReferredProspect): { when: string; what: string } {
  const now = new Date();
  const yest = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const time = p.lastActivity.toLocaleTimeString("es", { hour: "numeric", minute: "2-digit" });
  const days = Math.floor((now.getTime() - p.lastActivity.getTime()) / (24 * 60 * 60 * 1000));
  const when = sameDay(p.lastActivity, now)
    ? `Hoy, ${time}`
    : sameDay(p.lastActivity, yest)
      ? `Ayer, ${time}`
      : days < 14
        ? `Hace ${days} días`
        : p.lastActivity.toLocaleDateString("es", { day: "2-digit", month: "short" });
  const what =
    p.lastActivityKind === "test" ? "Completó el test personalizado" : p.lastActivityKind === "curso" ? "Avanzó en un curso" : "Se registró en la plataforma";
  return { when, what };
}

function growth(now: number, before: number): { pct: number | null } {
  if (before <= 0) return { pct: now > 0 ? 100 : null };
  return { pct: Math.round(((now - before) / before) * 100) };
}

function Pct({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-[11px] text-slate-400">Sin cambios</span>;
  const up = pct >= 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? "text-emerald-600" : "text-rose-500"}`}>
      <i className={up ? "ri-arrow-up-line" : "ri-arrow-down-line"}></i> {up ? "+" : ""}
      {pct}% vs. semana pasada
    </span>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} aria-label="Cerrar" className="w-8 h-8 rounded-full hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

export default function MisProspectos() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReferredProspect[]>([]);
  const [profiles, setProfiles] = useState<PersonalityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("todos");
  const [showFilters, setShowFilters] = useState(false);
  const [query, setQuery] = useState("");
  const [interest, setInterest] = useState<"" | InterestLevel>("");
  const [profileModal, setProfileModal] = useState<ReferredProspect | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [approved, setApproved] = useState<ApprovedMessage[] | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    let active = true;
    Promise.all([fetchReferredProspects(), fetchPersonalityProfiles().catch(() => [] as PersonalityInfo[])])
      .then(([r, p]) => {
        if (!active) return;
        setRows(r);
        setProfiles(p);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "No se pudieron cargar tus prospectos."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const archetype = (key: string | null) => profiles.find((p) => p.key === key) ?? null;

  const stats = useMemo(() => {
    const now = Date.now();
    const inWeek = (dt: Date | null, from: number, to: number) => !!dt && dt.getTime() >= from && dt.getTime() < to;
    const cur = now - WEEK;
    const prev = now - 2 * WEEK;
    const total = rows.length;
    const totalBefore = rows.filter((r) => r.createdAt.getTime() < cur).length;
    const newCur = rows.filter((r) => inWeek(r.createdAt, cur, now + 1)).length;
    const newPrev = rows.filter((r) => inWeek(r.createdAt, prev, cur)).length;
    const seg = rows.filter((r) => r.state === "seguimiento");
    const segCur = seg.filter((r) => inWeek(r.quizAt, cur, now + 1)).length;
    const segPrev = seg.filter((r) => inWeek(r.quizAt, prev, cur)).length;
    const hot = rows.filter((r) => r.state === "caliente");
    const hotCur = hot.filter((r) => inWeek(r.hotSince, cur, now + 1)).length;
    const hotPrev = hot.filter((r) => inWeek(r.hotSince, prev, cur)).length;
    return {
      total,
      totalGrowth: growth(total, totalBefore).pct,
      nuevos: newCur,
      nuevosGrowth: growth(newCur, newPrev).pct,
      seguimiento: seg.length,
      seguimientoGrowth: growth(segCur, segPrev).pct,
      calientes: hot.length,
      calientesGrowth: growth(hotCur, hotPrev).pct,
      inactivos: rows.filter((r) => r.state === "inactivo").length,
      nuevosTab: rows.filter((r) => r.state === "nuevo").length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab === "nuevo" && r.state !== "nuevo") return false;
      if (tab === "seguimiento" && r.state !== "seguimiento") return false;
      if (tab === "caliente" && r.state !== "caliente") return false;
      if (tab === "inactivo" && r.state !== "inactivo") return false;
      if (tab === "reuniones") return false;
      if (interest && r.interest !== interest) return false;
      if (q && !`${r.fullName} ${r.email}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, tab, query, interest]);

  const tabs: { key: TabKey; label: string; count: number }[] = [
    { key: "todos", label: "Todos", count: stats.total },
    { key: "nuevo", label: "Nuevos", count: stats.nuevosTab },
    { key: "seguimiento", label: "En seguimiento", count: stats.seguimiento },
    { key: "caliente", label: "Leads calientes", count: stats.calientes },
    { key: "reuniones", label: "Reuniones", count: 0 },
    { key: "inactivo", label: "No activos", count: stats.inactivos },
  ];

  const kpis = [
    { value: String(stats.total), label: "Total prospectos", icon: "ri-team-line", tint: "bg-violet-50 text-violet-600", pct: stats.totalGrowth },
    { value: String(stats.nuevos), label: "Nuevos esta semana", icon: "ri-eye-line", tint: "bg-sky-50 text-sky-600", pct: stats.nuevosGrowth },
    { value: String(stats.seguimiento), label: "En seguimiento", icon: "ri-line-chart-line", tint: "bg-emerald-50 text-emerald-600", pct: stats.seguimientoGrowth },
    { value: String(stats.calientes), label: "Leads calientes", icon: "ri-fire-fill", tint: "bg-orange-50 text-orange-500", pct: stats.calientesGrowth },
  ];

  const sendWhatsApp = (p: ReferredProspect) => {
    window.open(`https://wa.me/?text=${encodeURIComponent(suggestedMessage(p))}`, "_blank", "noopener,noreferrer");
  };

  const copyMessage = async (p: ReferredProspect) => {
    try {
      await navigator.clipboard.writeText(suggestedMessage(p));
      showToast("Mensaje copiado");
    } catch {
      showToast("No se pudo copiar.");
    }
    setMenuFor(null);
  };

  const openApproved = async () => {
    setApproved([]);
    try {
      setApproved((await fetchApprovedMessages()).filter((m) => m.active));
    } catch {
      showToast("No se pudieron cargar los mensajes.");
    }
  };

  return (
    <AffiliateShell>
      <div className="max-w-6xl mx-auto flex flex-col gap-4" onClick={() => menuFor && setMenuFor(null)}>
        {/* Encabezado */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A]">Mis Prospectos</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-md">Personas que han llegado a través de tu link y han mostrado interés en Vívelo AI.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
              className="px-5 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-700 text-sm font-semibold cursor-pointer whitespace-nowrap hover:border-violet-400"
            >
              <i className="ri-filter-3-line mr-1.5"></i>Filtros
            </button>
            <button
              onClick={() => navigate("/app/prospectar")}
              className="px-5 py-2.5 rounded-lg text-white text-sm font-semibold cursor-pointer whitespace-nowrap hover:opacity-90"
              style={{ background: PURPLE }}
            >
              <i className="ri-user-add-line mr-1.5"></i>Agregar prospecto
            </button>
          </div>
        </div>

        {showFilters ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
              <i className="ri-search-line text-slate-400"></i>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por nombre o correo…" className="flex-1 bg-transparent text-sm outline-none" />
            </div>
            <select value={interest} onChange={(e) => setInterest(e.target.value as "" | InterestLevel)} aria-label="Nivel de interés" className="border border-slate-200 rounded-lg px-3 py-2 text-sm cursor-pointer outline-none bg-white">
              <option value="">Todos los niveles de interés</option>
              <option value="muy_alto">Muy alto</option>
              <option value="alto">Alto</option>
              <option value="medio">Medio</option>
              <option value="bajo">Bajo</option>
            </select>
          </div>
        ) : null}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.tint}`}>
                  <i className={`${k.icon} text-xl`}></i>
                </span>
                <span className="font-heading font-extrabold text-3xl text-slate-900">{k.value}</span>
              </div>
              <div className="mt-2 text-xs font-semibold text-slate-700">{k.label}</div>
              <Pct pct={k.pct} />
            </div>
          ))}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-50 text-amber-500">
                <i className="ri-star-line text-xl"></i>
              </span>
              <span className="font-heading font-extrabold text-3xl text-slate-900">—</span>
            </div>
            <div className="mt-2 text-xs font-semibold text-slate-700">Reuniones agendadas</div>
            <span className="text-[11px] text-slate-400">Disponible al conectar Reuniones</span>
          </div>
        </div>

        {/* Pestañas */}
        <div className="bg-white border border-slate-200 rounded-2xl p-2 overflow-x-auto vv-scrollbar-hidden">
          <div className="flex gap-1 min-w-max" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={[
                  "px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
                  tab === t.key ? "bg-violet-50 border border-violet-300" : "text-slate-600 hover:bg-slate-50 border border-transparent",
                ].join(" ")}
                style={tab === t.key ? { color: PURPLE } : undefined}
              >
                {t.label} ({t.count})
              </button>
            ))}
          </div>
        </div>

        {/* Lista */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="hidden lg:grid grid-cols-[2.2fr_1.2fr_1.2fr_1.3fr_1.1fr_2.4fr_auto] gap-3 px-5 py-3 bg-violet-50/50 text-[11px] font-bold text-slate-600">
            <span>Prospecto</span>
            <span>Perfil</span>
            <span>Estado</span>
            <span>Última actividad</span>
            <span>Nivel de interés</span>
            <span>Mensaje sugerido</span>
            <span className="w-16"></span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <i className="ri-loader-4-line animate-spin text-2xl"></i>
              <p className="mt-2 text-sm">Cargando tus prospectos…</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center text-rose-600 text-sm">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">
              {rows.length === 0
                ? "Aún nadie se ha registrado con tu link. Compártelo desde tu Panel de Afiliado para empezar."
                : tab === "reuniones"
                  ? "Las reuniones agendadas aparecerán aquí cuando conectemos el módulo de Reuniones."
                  : "No hay prospectos que coincidan con tu filtro."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const st = STATE_UI[p.state];
                const it = INTEREST_UI[p.interest];
                const act = activityText(p);
                const arc = archetype(p.archetypeKey);
                return (
                  <li key={p.id} className="px-4 lg:px-5 py-4 grid grid-cols-1 lg:grid-cols-[2.2fr_1.2fr_1.2fr_1.3fr_1.1fr_2.4fr_auto] gap-3 lg:items-center">
                    <div className="flex items-center gap-3 min-w-0">
                      <MemberAvatar name={p.fullName} src={p.avatarUrl} />
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-slate-900 truncate">{p.fullName}</div>
                        <div className="text-[11px] text-slate-500 truncate">{p.email}</div>
                      </div>
                    </div>
                    <div className="text-xs text-slate-700">
                      {arc ? (
                        <>
                          <span className="font-semibold">{arc.label}</span>
                          <br />
                          <button onClick={() => setProfileModal(p)} className="font-semibold cursor-pointer" style={{ color: PURPLE }}>
                            Ver perfil
                          </button>
                        </>
                      ) : (
                        <span className="text-slate-400">Test pendiente</span>
                      )}
                    </div>
                    <div>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${st.cls}`}>
                        <i className={st.icon}></i> {st.label}
                      </span>
                    </div>
                    <div className="text-xs text-slate-700">
                      <div className="font-semibold">{act.when}</div>
                      <div className="text-slate-500">{act.what}</div>
                    </div>
                    <div>
                      <div className={`text-xs font-semibold flex items-center gap-1 ${it.color}`}>
                        <i className={it.icon}></i>
                        <span className="text-slate-800">{it.label}</span>
                      </div>
                      <div className="text-amber-400 text-xs" aria-label={`${p.stars} de 5`}>
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i key={i} className={i < p.stars ? "ri-star-fill" : "ri-star-line text-slate-300"}></i>
                        ))}
                      </div>
                    </div>
                    <p className="text-[11px] leading-snug text-slate-700 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">{suggestedMessage(p)}</p>
                    <div className="flex items-center gap-1 relative">
                      <button onClick={() => sendWhatsApp(p)} aria-label={`Escribir a ${p.fullName} por WhatsApp`} className="w-9 h-9 rounded-full text-[#20A464] hover:bg-emerald-50 cursor-pointer">
                        <i className="ri-whatsapp-line text-xl"></i>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuFor(menuFor === p.id ? null : p.id);
                        }}
                        aria-label="Más acciones"
                        className="w-9 h-9 rounded-full text-slate-500 hover:bg-slate-100 cursor-pointer"
                      >
                        <i className="ri-more-2-fill text-xl"></i>
                      </button>
                      {menuFor === p.id ? (
                        <div className="absolute right-0 top-10 z-10 w-44 bg-white border border-slate-200 rounded-xl shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => copyMessage(p)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                            <i className="ri-file-copy-line mr-2"></i>Copiar mensaje
                          </button>
                          {p.email ? (
                            <a href={`mailto:${p.email}`} className="block px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">
                              <i className="ri-mail-line mr-2"></i>Enviar correo
                            </a>
                          ) : null}
                          {arc ? (
                            <button
                              onClick={() => {
                                setProfileModal(p);
                                setMenuFor(null);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                            >
                              <i className="ri-user-3-line mr-2"></i>Ver perfil
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Tarjetas inferiores */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
            <span className="w-14 h-14 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: PURPLE }}>
              <i className="ri-lightbulb-flash-line text-2xl"></i>
            </span>
            <div className="flex-1">
              <h2 className="font-heading font-bold text-sm" style={{ color: PURPLE }}>
                Sugerencia de la Guía IA
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                {stats.calientes > 0
                  ? `Tienes ${stats.calientes} ${stats.calientes === 1 ? "lead caliente que podría estar listo" : "leads calientes que podrían estar listos"} para agendar una reunión. Enfócate en darles seguimiento hoy.`
                  : stats.total > 0
                    ? "Aún no tienes leads calientes. Escribe a quienes ya hicieron su test para invitarlos a empezar su primer curso."
                    : "Comparte tu link para recibir tus primeros prospectos. Yo te ayudo a escribirles."}
              </p>
              <button
                onClick={() => (stats.calientes > 0 ? setTab("caliente") : navigate("/app/guia-ia"))}
                className="mt-2 px-4 py-2 rounded-lg text-white text-xs font-semibold cursor-pointer"
                style={{ background: PURPLE }}
              >
                Ver recomendaciones <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4">
            <div className="flex-1">
              <h2 className="font-heading font-bold text-sm" style={{ color: PURPLE }}>
                Mensajes aprobados
              </h2>
              <p className="text-xs text-slate-600 mt-1">Usa mensajes que han sido aprobados para comunicarte con tus prospectos.</p>
            </div>
            <button onClick={openApproved} className="px-4 py-2 rounded-lg border text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-violet-50" style={{ color: PURPLE, borderColor: PURPLE }}>
              Ver mensajes <i className="ri-arrow-right-line"></i>
            </button>
          </section>
        </div>
      </div>

      {profileModal ? (
        <Modal title={`Perfil de ${profileModal.fullName}`} onClose={() => setProfileModal(null)}>
          {(() => {
            const arc = archetype(profileModal.archetypeKey);
            return arc ? (
              <div className="flex flex-col gap-3">
                <div>
                  <div className="text-lg font-heading font-extrabold text-slate-900">{arc.label}</div>
                  <div className="text-xs font-semibold" style={{ color: PURPLE }}>
                    Temperamento {arc.temperament}
                  </div>
                </div>
                <p className="text-sm text-slate-700">{arc.description}</p>
                <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                  Se registró el {profileModal.createdAt.toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })} · Avance máximo en cursos: {profileModal.maxPercent}%
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600">Esta persona todavía no completa el test de personalidad.</p>
            );
          })()}
        </Modal>
      ) : null}

      {approved ? (
        <Modal title="Mensajes aprobados" onClose={() => setApproved(null)}>
          <ul className="flex flex-col gap-3">
            {approved.map((m) => (
              <li key={m.id} className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs font-bold text-slate-900">{m.title}</div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{m.content}</p>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(m.content);
                      showToast("Mensaje copiado");
                    } catch {
                      showToast("No se pudo copiar.");
                    }
                  }}
                  className="mt-2 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold cursor-pointer"
                >
                  <i className="ri-file-copy-line mr-1"></i>Copiar
                </button>
              </li>
            ))}
            {approved.length === 0 ? <li className="text-sm text-slate-500 text-center py-4">Cargando mensajes…</li> : null}
          </ul>
        </Modal>
      ) : null}

      {toast ? (
        <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div>
      ) : null}
    </AffiliateShell>
  );
}
